import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, PageHead, Loading, ErrorLine } from "../components/ui";
import { useCampaign } from "../lib/campaign";
import { grantsArea } from "../lib/sections";
import { api } from "../lib/api";
import { Lnk } from "../components/Lnk";

type Toplines = {
  doors_knocked: number; surveys: number; answered_refused: number; not_home: number;
  impediments: number; total_attempted: number; canvassers: number; survey_rate: number;
};
type Signs = { needs_work: number; missing: number; planned: number; standing: number; fraud?: number };
// CALLS AND TEXTS ON HOME (2026-10-30): the same totals the Analytics tabs show (reports/phone_toplines).
type Phone = { calls?: { dials: number; talked: number; voicemail: number }; texts?: { sent: number; replies: number } };
type Cell = [string, string, string];

// ── WHAT THIS PERSON MAY SEE (0314) ──────────────────────────────────────────────────────────
// Home is the one screen every manager lands on, and it asks four endpoints across three sections.
// A manager fenced to Signs holds none of them, so before this Home fetched all four, collected the
// 403s into `apiErr`, and told him "Live metrics are temporarily unavailable" -- an OUTAGE banner
// for a permission answer, on the first screen he ever sees. Nothing was down. He simply does not
// have analytics.
//
// That is the empty-vs-error rule pointing the other way, and the repo has a CI guard about the
// first direction: a failure must not render as "no data", and NOT HAVING SOMETHING must not
// render as a failure. So Home asks only for what the grant allows, and a tile it may not have is
// absent rather than broken -- the same treatment a signs-only account already gets for Universes.
// The rule itself is lib/sections -- this page asked it with a `sections` array rather than the
// campaign, so the shim keeps the four call sites below reading as they did.
const has = (sections: string[] | undefined, area: string) => grantsArea({ sections }, area);

export function Home() {
  const { current, loading: campLoading } = useCampaign();
  const [today, setToday] = useState<Toplines | null>(null);
  const [all, setAll] = useState<Toplines | null>(null);
  const [apiErr, setApiErr] = useState<string | null>(null);
  // `counts` needs its own settled flag. It used to render a hardcoded "…" whenever it was null,
  // so a failed /campaign/summary left a spinner-ellipsis sitting where a number belongs — forever,
  // with no error anywhere on the page. "…" must mean IN FLIGHT and nothing else.
  const [counts, setCounts] = useState<{ universes: number; walkbooks: number } | null>(null);
  const [countsDone, setCountsDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [integ, setInteg] = useState<{ new: number; high_concern: number; open: number; field_checks_due: number } | null>(null);
  const [signs, setSigns] = useState<Signs | null>(null);
  const [phoneToday, setPhoneToday] = useState<Phone | null>(null);
  const [phoneAll, setPhoneAll] = useState<Phone | null>(null);

  useEffect(() => {
    if (!current) return;
    setBusy(true); setApiErr(null); setToday(null); setAll(null); setCounts(null); setInteg(null);
    setPhoneToday(null); setPhoneAll(null);
    setCountsDone(false);
    // campaign-local day so "today" matches the field team's clock, not UTC
    const tz = current.timezone || "America/Chicago";
    const day = new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date());
    const sec = current.sections;
    if (has(sec, "analytics")) {
      Promise.all([
        api<Toplines>(`/reports/toplines?campaign_id=${current.id}&date_from=${day}`).then(setToday),
        api<Toplines>(`/reports/toplines?campaign_id=${current.id}`).then(setAll),
      ]).catch((e) => setApiErr(String(e.message || e)));
    }
    // A campaign that calls or texts sees how much it did, today and all along, beside the doors. A
    // doors-only campaign gets no Calls or Texts tile: a "0 calls" there would describe a programme
    // that does not exist.
    if (has(sec, "analytics") && (current.calls_mode || current.texts_mode)) {
      const ask = async (q: string): Promise<Phone> => ({
        calls: current.calls_mode ? (await api<{ totals: Phone["calls"] }>(`/reports/calls?campaign_id=${current.id}${q}`)).totals : undefined,
        texts: current.texts_mode ? (await api<{ totals: Phone["texts"] }>(`/reports/texts?campaign_id=${current.id}${q}`)).totals : undefined,
      });
      // A failure settles to {} -- "—" plus the banner -- so "…" only ever means still on its way.
      ask(`&date_from=${day}&date_to=${day}`).then(setPhoneToday)
        .catch((e) => { setApiErr((prev) => prev || String(e.message || e)); setPhoneToday({}); });
      ask("").then(setPhoneAll)
        .catch((e) => { setApiErr((prev) => prev || String(e.message || e)); setPhoneAll({}); });
    }
    if (has(sec, "account")) {
      api<{ universes: number; walkbooks: number }>(`/campaign/summary?campaign_id=${current.id}`)
        .then(setCounts).catch((e) => setApiErr((prev) => prev || String(e.message || e)))
        .finally(() => { setCountsDone(true); setBusy(false); });
    } else { setCountsDone(true); setBusy(false); }
    // integrity action-inbox — manager-only; the pill mirrors the REVIEW QUEUE only (status=NEW)
    if (has(sec, "integrity")) {
      api<{ new: number; high_concern: number; open: number; field_checks_due: number }>(`/integrity/summary?campaign_id=${current.id}`)
        .then(setInteg).catch(() => {});
    }
    // THE SIGN PILL. Caleb, 2026-09-24: "a manager signs in and is immediately flagged that there's
    // yard signs in need of attention in their district." Only on a campaign that runs signs at all
    // -- a pill reading "0 need attention" on a doors-only campaign is a promise about a programme
    // that does not exist.
    if (current.places_enabled && has(sec, "signs")) {
      api<Signs>(`/signs/attention?campaign_id=${current.id}`).then(setSigns).catch(() => {});
    }
  }, [current?.id]);

  if (campLoading) return <Loading />;
  if (!current) return <div className="text-muted">No campaign assigned to your account yet. Ask an admin to add you to a campaign.</div>;

  const pct = (n: number) => `${Math.round(n * 100)}%`;
  const phoneCells = (p: Phone | null): Cell[] => [
    ...(current.calls_mode ? [["Calls", p?.calls ? p.calls.dials.toLocaleString() : p === null ? "…" : "—",
        p?.calls ? `${p.calls.talked.toLocaleString()} talked · ${p.calls.voicemail.toLocaleString()} voicemails` : ""] as Cell] : []),
    ...(current.texts_mode ? [["Texts", p?.texts ? p.texts.sent.toLocaleString() : p === null ? "…" : "—",
        p?.texts ? `${p.texts.replies.toLocaleString()} replies` : ""] as Cell] : []),
  ];
  const sees = has(current.sections, "analytics");
  const dash = busy ? "…" : "—";
  const core = (tl: Toplines | null): Cell[] => [
    ["Doors knocked", tl ? tl.doors_knocked.toLocaleString() : dash, tl ? `${tl.total_attempted.toLocaleString()} attempted` : ""],
    ["Surveys", tl ? tl.surveys.toLocaleString() : dash, tl ? `${pct(tl.survey_rate)} household survey rate` : ""],
    ["Not home", tl ? tl.not_home.toLocaleString() : dash, tl ? `${tl.answered_refused.toLocaleString()} answered/refused` : ""],
    ["Crew", tl ? String(tl.canvassers) : dash, tl ? `${tl.impediments} impediments` : ""],
  ];
  // A SIGNS-ONLY ACCOUNT HAS NO UNIVERSES TO COUNT (0307). Leaving the tile in would report a true
  // zero that reads as a broken feature rather than an absent one.
  const hasVoters = current.voters_enabled !== false;
  const campaignCells: Cell[] = [
    ...core(all),
    ...(sees ? phoneCells(phoneAll) : []),
    ...(hasVoters
      ? [["Universes", counts ? String(counts.universes) : countsDone ? "—" : "…", counts ? `${counts.walkbooks} walkbooks` : ""] as Cell]
      : []),
    // THE WORK THE SIGN PROGRAMME HAS DONE, which nothing on this page reported until now: "we've
    // got the mechanism to do the work but nothing that's really tracking the work." Standing is
    // the headline because it is the answer to "how many signs do we have out", and planned rides
    // underneath because a corner with no board on it is not a sign and must never be added in.
    ...(current.places_enabled && signs
      ? [["Signs standing", signs.standing.toLocaleString(),
          signs.planned ? `${signs.planned} planned, no board yet` : "none planned"] as Cell]
      : []),
    ["Status", current.status, ""],
  ];

  return (
    <>
      <PageHead title="Home" sub={`${current.name}${current.state ? ` · ${current.state}` : ""}`} />
      {apiErr && (
        <ErrorLine className="mb-2.5" message="Live numbers couldn't load right now. Refresh in a minute." />
      )}
      {integ && (integ.high_concern > 0 || integ.new > 0) && (() => {
        // Review-queue only (status=NEW). Once a flag is reviewed/investigated it leaves the queue and
        // drops off this pill; open investigations + field checks live on the Integrity page.
        const rest = integ.new ? `${integ.new} flag${integ.new === 1 ? "" : "s"} to review` : "";
        const hot = integ.high_concern > 0;
        return (
          <div className="mb-3">
            <Link to="/integrity?tab=review" title="Open the integrity review queue"
              className={`inline-flex items-center gap-2.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold no-underline transition-colors ${hot ? "border-bad/50 bg-bad/10 text-bad hover:bg-bad/20" : "border-warn/50 bg-warn/10 text-warn hover:bg-warn/20"}`}>
              <span className="uppercase tracking-wide opacity-80">Integrity</span>
              {hot && <span className="font-bold">⚠ {integ.high_concern} high-concern</span>}
              {rest && <span className={hot ? "opacity-90" : ""}>{rest}</span>}
              <span className="opacity-90">→</span>
            </Link>
          </div>
        );
      })()}

      {/* ── SIGNS THAT WANT SOMEBODY ────────────────────────────────────────────────────────
          Its own pill, not a second line inside the integrity one. They answer different questions
          and go to different screens: integrity is "did somebody record this honestly", signs is
          "is there a board on the ground that needs fixing". Folding them together would send one
          click to two places.

          NO DATE RANGE behind it, and that is the point: everything else on this page is today or
          all-time because a knock is an EVENT. A sign in the ground is a STATE. A board that went
          missing three weeks ago is still missing when he opens the laptop, and a pill that only
          counted today would be empty every morning and teach him to ignore it.

          Red when anything is MISSING -- no board is there at all -- and amber when boards are
          standing but damaged. Same distinction the map's dots make, in the same direction. */}
      {signs && (signs.needs_work > 0 || (signs.fraud ?? 0) > 0) && (() => {
        // CONFIRMED FRAUD IS ITS OWN NUMBER (0347), between missing and repair and inside neither --
        // Caleb: "we just need to clearly indicate which ones need work and which ones are
        // fraudulent". Red, like missing, because it is the same trip: go and see, take a sign.
        const fraud = signs.fraud ?? 0;
        const gone = signs.missing > 0 || fraud > 0;
        const rest = signs.needs_work - signs.missing;
        return (
          <div className="mb-3">
            <Link to="/signs?lens=wants" title="Open the signs that need somebody"
              className={`inline-flex items-center gap-2.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold no-underline transition-colors ${gone ? "border-bad/50 bg-bad/10 text-bad hover:bg-bad/20" : "border-warn/50 bg-warn/10 text-warn hover:bg-warn/20"}`}>
              <span className="uppercase tracking-wide opacity-80">Signs</span>
              {signs.missing > 0 && <span className="font-bold">⚠ {signs.missing} missing</span>}
              {fraud > 0 && <span className="font-bold">⚑ {fraud} fraud</span>}
              {rest > 0 && <span className={gone ? "opacity-90" : ""}>
                {rest} need{rest === 1 ? "s" : ""} repair or replacing
              </span>}
              <span className="opacity-90">→</span>
            </Link>
          </div>
        );
      })()}

      <div className="flex items-center justify-between mb-1.5">
        <div className="text-2xs font-bold uppercase tracking-wide text-muted">Today</div>
        <Lnk to="/analytics?tab=map" className="text-xs font-semibold">Live results →</Lnk>
      </div>
      <StatStrip cells={[...core(today), ...(sees ? phoneCells(phoneToday) : [])]} className="mb-4" />

      <div className="text-2xs font-bold uppercase tracking-wide text-muted mb-1.5">Campaign · all time</div>
      <StatStrip cells={campaignCells} />
    </>
  );
}

function StatStrip({ cells, className = "" }: { cells: Cell[]; className?: string }) {
  return (
    <Card className={`flex overflow-hidden ${className}`}>
      {cells.map(([l, n, d], i) => (
        <div key={l} className={`flex-1 p-2 ${i > 0 ? "border-l border-border" : ""}`}>
          <div className="text-muted text-2xs font-bold uppercase tracking-wide">{l}</div>
          <div className="text-lg font-extrabold mt-0.5 tracking-tight">{n}</div>
          <div className="text-2xs font-bold mt-0.5 text-muted">{d || " "}</div>
        </div>
      ))}
    </Card>
  );
}
